import React from 'react';
import { apiFetch } from '../utils/api';
import '../styles/ProjectDetail.css';
import '../styles/_colors.css';
import '../styles/_typography.css';
import '../styles/main.css';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

// Removed BACKEND_URL, use apiFetch instead

export default function ProjectDetail() {
  const { name } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const viewTracked = useRef(false);

  useEffect(() => {
    apiFetch(`/api/projects?name=${name}&exact=true`)
      .then(data => {
        setProject(data.projects[0]);
        setLoading(false);
        // Track project view only once to prevent double counting in StrictMode
        if (data.projects[0] && !viewTracked.current) {
          viewTracked.current = true;
          apiFetch(`/api/projects/${data.projects[0].name}/view`, { method: 'POST' })
            .catch(err => console.warn('Failed to track view:', err));
        }
      })
      .catch(err => {
        console.error('Error fetching project:', err);
        setLoading(false);
      });
  }, [name]);

  // Reset view tracking when project name changes
  useEffect(() => {
    viewTracked.current = false;
  }, [name]);

  if (loading) {
    return (
      <div className="loading-container">
        Loading project details...
      </div>
    );
  }

  if (!project) {
    return (
      <div className="loading-container">
        Project not found
      </div>
    );
  }

  return (
    <div className="project-detail-container">
      <Helmet>
        <title>{`NHHS Analytics | ${project.displayName}`}</title>
      </Helmet>
      <div className="project-detail-content">
        {/* Article Content - Natural Flow with title integrated */}
        <div className="project-article">
          <div className="project-article-content">
            {/* Title centered without label */}
            <h1 className="article-title">
              {project.displayName || project.name}
            </h1>
            
            {/* Introduction flows naturally after title */}
            {(project.introduction || project.description) && (
              <>
                {(project.introduction || project.description).split('\n').map((paragraph, pIndex) => (
                  paragraph.trim() && (
                    <p key={`intro-${pIndex}`} className="article-text article-intro">{paragraph.trim()}</p>
                  )
                ))}
              </>
            )}
            
            {/* Rest of the article content flows naturally */}
            {project.sections && project.sections.length > 0 && (
              <>
                {project.sections.map((section, sectionIndex) => (
                  <React.Fragment key={sectionIndex}>
                    {section.subtitle && (
                      <h3 className="article-heading">{section.subtitle}</h3>
                    )}
                    
                    {section.body && (
                      <>
                        {section.body.split('\n').map((paragraph, pIndex) => (
                          paragraph.trim() && (
                            <p key={`section-${sectionIndex}-p-${pIndex}`} className="article-text">{paragraph.trim()}</p>
                          )
                        ))}
                      </>
                    )}
                    
                    {section.images && section.images.length > 0 && (
                      <div className={`article-images ${section.images.length > 1 ? 'multiple-images' : 'single-image'}`}>
                        {section.images.map((image, imgIndex) => (
                          <figure key={`section-${sectionIndex}-img-${imgIndex}`} className="article-figure">
                            <img
                              src={image}
                              alt={section.subtitle ? section.subtitle : 'Project'}
                              className="article-image"
                            />
                          </figure>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
            
            {/* PDF download link placed after content but before metadata */}
            {project.pdfUrl && (
              <div className="pdf-download-section">
                <a href={project.pdfUrl} target="_blank" rel="noopener noreferrer" className="pdf-download-link">
                  📄 Download project documentation (PDF)
                </a>
              </div>
            )}
            
            {/* Metadata flows naturally at the end of the article */}
            <div className="article-footer">
              <hr className="article-divider" />
              <div className="article-meta">
                <div className="article-meta-grid">
                  <div className="article-meta-item">
                    <span className="article-meta-label">Category</span>
                    <span className="article-meta-value">{project.category || 'Uncategorized'}</span>
                  </div>
                  {project.createdAt && (
                    <div className="article-meta-item">
                      <span className="article-meta-label">Created</span>
                      <span className="article-meta-value">
                        {new Date(project.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {project.status && (
                    <div className="article-meta-item">
                      <span className="article-meta-label">Status</span>
                      <span className="article-meta-value">{project.status}</span>
                    </div>
                  )}
                  {project.views && (
                    <div className="article-meta-item">
                      <span className="article-meta-label">Views</span>
                      <span className="article-meta-value">{project.views} time{project.views !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Action links flow naturally */}
            <div className="article-actions">
              {project.githubUrl && (
                <p className="article-text">
                  <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="article-link">
                    View the source code on GitHub
                  </a>
                </p>
              )}
              {project.liveUrl && (
                <p className="article-text">
                  <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="article-link">
                    Try the live demo
                  </a>
                </p>
              )}
              <p className="article-text">
                <Link to="/projects" className="article-link">← Back to all projects</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}